import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home as HomeIcon, Heart, ShoppingBag, Truck } from 'lucide-react';
import { useCart } from '../context/CartContext';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Navbar from '../components/Navbar';
import HeroSlider from '../components/HeroSlider';
import CategorySection from '../components/CategorySection';
import ProductGrid from '../components/ProductGrid';
import FeaturesSection from '../components/FeaturesSection';
import BottomFeatures from '../components/BottomFeatures';
import Footer from '../components/Footer';
import { API_URL } from '../config';
import '../components/CartDrawer.css';
import './Home.css';

const defaultProducts = [
  {
    id: 1,
    title: 'California Almonds (Premium)',
    rating: 5.0,
    reviewsCount: 148,
    price: 499,
    oldPrice: 599,
    image: 'https://images.unsplash.com/photo-1508061253366-f7da158b6d46?auto=format&fit=crop&q=80&w=400',
    badge: 'Best Seller',
    type: 'nuts'
  },
  {
    id: 2,
    title: 'Premium Whole Cashews',
    rating: 4.9,
    reviewsCount: 96,
    price: 649,
    oldPrice: 799,
    image: 'https://images.unsplash.com/photo-1600002415506-dd0609260c18?auto=format&fit=crop&q=80&w=400',
    badge: '15% OFF',
    type: 'nuts'
  },
  {
    id: 3,
    title: 'Kashmiri Walnuts (Snow White halves)',
    rating: 4.8,
    reviewsCount: 112,
    price: 599,
    oldPrice: 749,
    image: 'https://images.unsplash.com/photo-1601004890684-d8cbf643f5f2?auto=format&fit=crop&q=80&w=400',
    badge: 'Direct from Farm',
    type: 'nuts'
  },
  {
    id: 4,
    title: 'Slow Roasted Makhana (Fox Nuts)',
    rating: 5.0,
    reviewsCount: 88,
    price: 199,
    oldPrice: 249,
    image: 'https://images.unsplash.com/photo-1596547609652-9cf5d8d76921?auto=format&fit=crop&q=80&w=400',
    badge: 'Healthy Snacking',
    type: 'makhana'
  },
  {
    id: 5,
    title: 'Spicy Peri Peri Roasted Makhana',
    rating: 4.7,
    reviewsCount: 74,
    price: 229,
    oldPrice: 299,
    image: 'https://images.unsplash.com/photo-1596547609652-9cf5d8d76921?auto=format&fit=crop&q=80&w=400',
    badge: 'Trending',
    type: 'makhana'
  },
  {
    id: 6,
    title: 'Golden Raisins (Premium Kishmish)',
    rating: 4.9,
    reviewsCount: 65,
    price: 249,
    oldPrice: 299,
    image: 'https://images.unsplash.com/photo-1568254183919-78a4f43a2877?auto=format&fit=crop&q=80&w=400',
    badge: 'Sweet & Plump',
    type: 'raisins'
  }
];

const Home = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState(defaultProducts);
  const [activeTab, setActiveTab] = useState('Home');

  const {
    cartCount,
    cartTotal,
    wishlistCount,
    handleAddToCart,
    handleToggleWishlist
  } = useCart();

  useEffect(() => {
    fetch(`${API_URL}/products`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load products from database');
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setProducts(data);
        }
      })
      .catch((err) => console.error('Error fetching products:', err));
  }, []);

  return (
    <div className="homepage-wrapper">
      {/* 1. Top Announcement Bar */}
      <TopBar />

      {/* 2. Main Header */}
      <Header 
        wishlistCount={wishlistCount} 
        cartCount={cartCount} 
        onCartClick={() => navigate('/cart')} 
      />

      {/* 3. Navigation Bar */}
      <Navbar activeItem="Shop by Category" />

      {/* Main Sections */}
      <main>
        {/* 4. Hero Banner Slider */}
        <HeroSlider />

        {/* 5. Shop by Category */}
        <CategorySection />

        {/* 6. Best Selling Products */}
        <ProductGrid 
          title="Best Selling Products"
          id="best-sellers"
          lightBg={true}
          products={products.filter(p => p.reviewsCount > 100).slice(0, 6)}
          onAddToCart={handleAddToCart} 
          onToggleWishlist={handleToggleWishlist} 
        />

        {/* 6b. Makhana Specials Section */}
        <ProductGrid 
          title="Makhana Specials"
          typeFilter="makhana"
          id="makhana-specials"
          lightBg={false}
          products={products}
          onAddToCart={handleAddToCart}
          onToggleWishlist={handleToggleWishlist}
        />

        {/* 6c. Combo Offers Section */}
        <ProductGrid 
          title="Combo Offers"
          typeFilter="combo"
          id="combo-offers"
          lightBg={true}
          products={products}
          onAddToCart={handleAddToCart}
          onToggleWishlist={handleToggleWishlist}
        />

        {/* 6c. Premium Nuts Section */}
        <ProductGrid 
          title="Premium Nuts"
          typeFilter="nuts"
          id="premium-nuts"
          lightBg={false}
          products={products}
          onAddToCart={handleAddToCart}
          onToggleWishlist={handleToggleWishlist}
        />

        {/* 7. Benefits Banner */}
        <FeaturesSection />

        {/* 8. Service Features */}
        <BottomFeatures />
      </main>

      {/* 9. Footer */}
      <Footer />

      {/* Optional: Sticky Bottom Navigation (Mobile screens only) */}
      <div className="mobile-sticky-nav" role="navigation" aria-label="Mobile Navigation">
        <a 
          href="#home" 
          className={`mobile-nav-item ${activeTab === 'Home' ? 'active' : ''}`}
          onClick={() => setActiveTab('Home')}
        >
          <HomeIcon size={20} />
          <span>Home</span>
        </a>
        <a 
          href="/track" 
          className={`mobile-nav-item ${activeTab === 'Track' ? 'active' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            setActiveTab('Track');
            navigate('/track');
          }}
        >
          <Truck size={20} />
          <span>Track Order</span>
        </a>
        <a 
          href="/wishlist" 
          className={`mobile-nav-item ${activeTab === 'Wishlist' ? 'active' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            setActiveTab('Wishlist');
            navigate('/wishlist');
          }}
        >
          <Heart size={20} />
          {wishlistCount > 0 && (
            <span className="badge-count">{wishlistCount}</span>
          )}
          <span>Wishlist</span>
        </a>
        <a 
          href="#cart" 
          className={`mobile-nav-item ${activeTab === 'Cart' ? 'active' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            setActiveTab('Cart');
            navigate('/cart');
          }}
        >
          <ShoppingBag size={20} />
          {cartCount > 0 && (
            <span className="badge-count">{cartCount}</span>
          )}
          <span>Cart</span>
        </a>
      </div>
      {/* Floating Cart Widget (Domino's style) */}
      {cartCount > 0 && (
        <div className="floating-cart-bar" onClick={() => navigate('/cart')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ShoppingBag size={20} />
            <div className="floating-cart-info">
              <span className="floating-cart-count">{cartCount} {cartCount === 1 ? 'Item' : 'Items'}</span>
              <span className="floating-cart-total">₹{cartTotal}</span>
            </div>
          </div>
          <span className="floating-cart-btn">
            <span>View Cart</span>
          </span>
        </div>
      )}
    </div>
  );
};

export default Home;
